/**
 * T3-1.3：图案裁剪 Worker API HTTP 错误码 → 前端行为映射。
 *
 * | HTTP | 行为 |
 * | 429  | toast「系统繁忙，请稍后重试」 |
 * | 403  | toast「无权操作该任务」（积分不足等同 403 时跳过，保留原消息） |
 * | 502  | 标记 failed，可单张重试 |
 * | 503  | 标记 failed（Qwen 主路径不可用），可单张重试 |
 */

import { isCreditsInsufficientError } from '@/utils/api';

export const IMAGE_CROP_TOAST_BUSY = '系统繁忙，请稍后重试';
export const IMAGE_CROP_TOAST_FORBIDDEN = '无权操作该任务';
export const IMAGE_CROP_FAILED_GATEWAY = '服务暂不可用，请稍后重试单张';
export const IMAGE_CROP_FAILED_UNAVAILABLE = '图案裁剪服务暂不可用，请稍后再试';
export const IMAGE_CROP_POLL_FAILED = '进度刷新失败，请稍后重试';

/** 后端 {@code ImageCropBusyException} code → HTTP status（status 缺失时的兜底） */
const IMAGE_CROP_ERROR_CODE_TO_STATUS = {
  IMAGE_CROP_QWEN_BUSY: 429,
  IMAGE_CROP_UNAVAILABLE: 503,
};

const IMAGE_CROP_HTTP_ERROR_MAP = {
  429: {
    toast: IMAGE_CROP_TOAST_BUSY,
    markFailed: false,
    retryable: true,
  },
  403: {
    toast: IMAGE_CROP_TOAST_FORBIDDEN,
    markFailed: false,
    retryable: false,
  },
  502: {
    toast: null,
    failedMessage: IMAGE_CROP_FAILED_GATEWAY,
    markFailed: true,
    retryable: true,
  },
  503: {
    toast: null,
    failedMessage: IMAGE_CROP_FAILED_UNAVAILABLE,
    markFailed: true,
    retryable: true,
  },
};

function resolveImageCropHttpStatus(error) {
  const status = Number(error?.status);
  if (Number.isFinite(status) && status > 0) return status;
  const code = String(error?.code ?? '').trim();
  return IMAGE_CROP_ERROR_CODE_TO_STATUS[code] ?? null;
}

/**
 * @returns {null | {
 *   status: number,
 *   toast: string | null,
 *   failedMessage?: string,
 *   markFailed: boolean,
 *   retryable: boolean,
 * }}
 */
export function resolveImageCropHttpError(error) {
  const status = resolveImageCropHttpStatus(error);
  if (!status) return null;
  // 403 也可能是积分不足（CreditsInsufficientException），保留原消息与全局 handler 行为
  if (status === 403 && isCreditsInsufficientError(error)) return null;
  const mapping = IMAGE_CROP_HTTP_ERROR_MAP[status];
  if (!mapping) return null;
  return {
    status,
    ...mapping,
  };
}

export function isImageCropMappedHttpError(error) {
  return resolveImageCropHttpError(error) != null;
}

export function isImageCropRetryableHttpError(error) {
  return Boolean(resolveImageCropHttpError(error)?.retryable);
}

/** 轮询不可恢复：必须停表、标记失败，禁止继续打 API */
const IMAGE_CROP_FATAL_POLL_STATUSES = new Set([400, 404, 410]);

export function resolveImageCropHttpStatusCode(error) {
  return resolveImageCropHttpStatus(error);
}

export function isImageCropFatalPollError(error) {
  const status = resolveImageCropHttpStatus(error);
  return status != null && IMAGE_CROP_FATAL_POLL_STATUSES.has(status);
}

/**
 * 将 API 错误 enrich 为带 imageCropError 元数据的 Error（按 status 覆盖用户可见文案）。
 */
export function enrichImageCropApiError(error) {
  const mapping = resolveImageCropHttpError(error);
  const base = error instanceof Error ? error : new Error(String(error ?? '操作失败'));
  if (!mapping) {
    base.imageCropError = null;
    return base;
  }

  const userMessage = mapping.toast || mapping.failedMessage || base.message || '操作失败';
  const next = new Error(userMessage);
  next.status = mapping.status;
  next.code = base.code;
  next.payload = base.payload;
  next.retryAfterSeconds = base.retryAfterSeconds;
  next.rawMessage = base.rawMessage || base.message;
  next.ipeIntercepted = base.ipeIntercepted;
  next.imageCropError = mapping;
  return next;
}

/**
 * 按映射执行副作用（toast），返回 mapping；无映射时返回 null。
 * @param {Function} [showToast] (message, type?) => void
 */
export function applyImageCropApiErrorSideEffects(error, { showToast } = {}) {
  const mapping = error?.imageCropError || resolveImageCropHttpError(error);
  if (!mapping?.toast || typeof showToast !== 'function') {
    return mapping;
  }
  showToast(mapping.toast, 'warning');
  return mapping;
}

/**
 * enrich + 可选 toast，供 task API 与页面 catch 复用。
 */
export function presentImageCropApiError(error, options = {}) {
  const enriched = enrichImageCropApiError(error);
  applyImageCropApiErrorSideEffects(enriched, options);
  return enriched;
}

/**
 * 轮询进度失败：429 可提示「繁忙」；404/400 等致命错误不弹 toast（由调用方停轮询并改 UI）。
 * @param {Function} [options.showToast] (message, type?) => void
 * @param {boolean} [options.silent] 完全不弹 toast
 */
export function presentImageCropPollError(error, { showToast, silent = false } = {}) {
  const enriched = enrichImageCropApiError(error);
  const mapping = enriched.imageCropError || resolveImageCropHttpError(enriched);
  const fatal = isImageCropFatalPollError(enriched);
  enriched.pollError = true;
  enriched.pollFatal = fatal;

  if (silent || fatal) {
    return enriched;
  }

  if (mapping) {
    const message = mapping.toast || mapping.failedMessage;
    if (message && typeof showToast === 'function') {
      showToast(message, 'warning');
    }
    return enriched;
  }

  if (typeof showToast === 'function') {
    showToast(IMAGE_CROP_POLL_FAILED, 'warning');
  }
  const fallback = new Error(IMAGE_CROP_POLL_FAILED);
  if (error instanceof Error) {
    fallback.cause = error;
    if (error.status != null) fallback.status = error.status;
    if (error.code) fallback.code = error.code;
  } else if (error && typeof error === 'object') {
    if (error.status != null) fallback.status = error.status;
    if (error.code) fallback.code = error.code;
  }
  fallback.pollError = true;
  fallback.pollFatal = false;
  return fallback;
}

export function resolveImageCropFailedMessage(error, fallback = '重试失败') {
  const mapping = error?.imageCropError || resolveImageCropHttpError(error);
  if (mapping?.failedMessage) return mapping.failedMessage;
  if (mapping?.toast) return mapping.toast;
  return error?.message || fallback;
}

export function shouldMarkImageCropItemFailed(error) {
  if (isCreditsInsufficientError(error)) return false;
  const mapping = error?.imageCropError || resolveImageCropHttpError(error);
  if (!mapping) return true;
  return mapping.markFailed !== false;
}
